import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { DataPanelInlineItem } from '@/ui';
import { useAgencyConfiguration } from '@/app/shell/agencyConfiguration';
import type { IncidentAgency } from '@/signalr';
import { orderShowFirst, agencyIdEquals } from '@ngsos/portal-core/incidents';
import { InvolvedAgenciesDetailDialog, type InvolvedAgenciesDetailDialogHandle } from '../../dialogs/InvolvedAgenciesDetailDialog';
import { InvolvedAgencyStateComponent } from './InvolvedAgencyStateComponent';
import type { InvolvedAgencyStateInfo } from '@ngsos/portal-core/incidents';

export type InvolvedAgenciesPanelItemProps = {
  incidentInvolvedAgencyStates: readonly IncidentAgency[];
  createdTimeUtc: string;
  closedTimeUtc: string | null;
  incidentIsReadOnly: boolean;
};

export function InvolvedAgenciesPanelItem({ incidentInvolvedAgencyStates }: InvolvedAgenciesPanelItemProps) {
  const { t } = useTranslation();
  const agencyConfiguration = useAgencyConfiguration();
  const detailDialogRef = useRef<InvolvedAgenciesDetailDialogHandle>(null);

  const involvedAgencyStatesWithNames: InvolvedAgencyStateInfo[] = useMemo(
    () =>
      orderShowFirst(incidentInvolvedAgencyStates, (agency) => agencyIdEquals(agency.agencyId, agencyConfiguration.id)).map(
        (agency) => ({
          agencyId: agency.agencyId,
          agencyName: agency.displayName,
          currentState: { timestampUtc: agency.currentStateSinceUtc, state: agency.currentState },
          statusHistory: agency.statusHistory,
        }),
      ),
    [incidentInvolvedAgencyStates, agencyConfiguration.id],
  );

  const involvedOtherAgencyStatesWithNames = involvedAgencyStatesWithNames.filter(
    (agency) => !agencyIdEquals(agency.agencyId, agencyConfiguration.id),
  );

  if (involvedOtherAgencyStatesWithNames.length === 0) {
    return null;
  }

  return (
    <>
      <DataPanelInlineItem
        title={t('Incident_InfoPanel_OtherInvolvedAgencies')}
        clickable
        onPanelClick={() => detailDialogRef.current?.show(involvedAgencyStatesWithNames)}
        hoverTooltip={t('Tooltip_ShowDetails')}
      >
        <div className="d-flex gap-1 flex-wrap justify-content-end">
          {involvedOtherAgencyStatesWithNames.map((item) => (
            <InvolvedAgencyStateComponent
              key={item.agencyId}
              collaborationState={item.currentState.state}
              agencyName={item.agencyName}
            />
          ))}
        </div>
      </DataPanelInlineItem>

      <InvolvedAgenciesDetailDialog ref={detailDialogRef} />
    </>
  );
}
